<template>
  <BaseDraggableModal
      :name="modal_name"
      max-height="80vh"
      width="70vw"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :ref="modal_name">
    <v-card :loading="loading" :disabled="loading">
      <BaseModal>
        <v-toolbar class="draggable_selector" dark color="primary" dense flat slot="header">
          <v-btn icon @click.stop="closeModal(modal_name)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ page.title }}</v-toolbar-title>
          <v-spacer/>
          <v-btn v-if="canDelete" text small @click.stop="archive">
            <v-icon>mdi-trash-can</v-icon>
          </v-btn>
          <v-btn v-if="canEdit" text small @click.stop="save">
            <v-icon>mdi-content-save</v-icon>
          </v-btn>
        </v-toolbar>
        <div slot="content">
            <v-divider/>
            <v-card-text>
              <!--USUAL FIELDS-->
              <base-field
                  v-for="field in usual_fields"
                  :key="field.name"
                  :isDisabled="!canEdit"
                  v-model="record[field.name]"
                  :field="field"
                  @change="usualFieldDataChanged"/>
              <!--CUSTOM FIELDS-->
              <template v-if="record.custom_fields">
                <base-field
                    v-for="field in custom_fields"
                    :key="field.name"
                    :isDisabled="!canEdit"
                    v-model="record.custom_fields[field.name]"
                    :field="field"
                    @change="customFieldDataChanged"/>
              </template>
              <!--LIST FILES-->
              <template v-if="record.files">
                <base-row
                    v-for="(file, index) in record.files" :key="index"
                    class="mt-3"
                    :label="$store.getters.translate('file')"
                    v-model="file.name"
                    @click="downloadFile(file)"
                    :link="true"
                    :hide_copy="true"
                    :deletable="canEdit"
                    @delete="deleteFile(file)"/>
              </template>
              <template v-else-if="record.file">
                <base-row
                    class="mt-3"
                    :label="$store.getters.translate('file')"
                    v-model="record.file.name"
                    @click="downloadFile(record.file)"
                    :link="true"
                    :hide_copy="true"
                    :deletable="canEdit"
                    @delete="deleteFile(record.file)"/>
              </template>
              <!--UPLOAD FILES-->
              <v-row v-if="files_required">
                <v-col v-cloak @drop.prevent="addDropFile" @dragover.prevent>
                  <v-file-input
                      :disabled="!canEdit"
                      class="mt-3"
                      :label="$store.getters.translate('files')"
                      placeholder="Drop file to upload"
                      show-size
                      v-model="new_files"
                      :multiple="multiple_files"
                      @change="saveFiles"/>
                </v-col>
              </v-row>
              <!--TAGS-->
              <base-tags v-if="tags_required" v-model="record.tags" :module_name="page.name"/>
            </v-card-text>
        </div>
      </BaseModal>
    </v-card>
  </BaseDraggableModal>
</template>

<script>
import helpFunctions from "../../plugins/helpFunctions";
import BaseModal from "../../components/commonComponents/BaseModal";
import BaseDraggableModal from "../../components/commonComponents/BaseDraggableModal";

export default {
  props: ["modal_name", "page", "files_required", "multiple_files"],
  components: {
    BaseModal,
    BaseDraggableModal,
  },
  data() {
    return {
      record: {},
      loading: false,
      usual_fields: [],
      custom_fields: [],
      tags_required: false,
      new_files: []
    };
  },
  methods: {
    ...helpFunctions.modal_functions,
    async load(id) {
      this.record = {};
      this.new_files = [];
      this.usual_fields = [];
      this.custom_fields = [];
      this.openModal(this.modal_name);
      this.loading = true;
      this.record = await helpFunctions.loadModel(this.$toasted, this.page.name, id);
      if(this.page.model === 'tag') {
        this.record.name = this.record.name.en;
      }
      await this.getFields();
    },
    async save() {
      if(!this.loading) {
        if (helpFunctions.modelFieldsFilled(this.$toasted, this.record, this.usual_fields)) {
          this.loading = true;
          if(await helpFunctions.saveModel(this.$toasted, this.page.name, this.record)) {
            if(this.page.model === 'translation') {
              await this.$store.dispatch("refreshTranslations");
            }
            this.$emit("refresh");
            this.closeModal(this.modal_name);
          }
          this.loading = false;
        }
      }
    },
    async archive() {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then(async (res) => {
        if (res) {
          this.loading = true;
          if (await helpFunctions.deleteModel(this.$toasted, this.page.name, this.record.id)) {
            this.$emit("refresh");
            this.closeModal(this.modal_name);
          }
          this.loading = false;
        }
      });
    },
    usualFieldDataChanged(data) {
      if(this.record[data[0]] !== data[1]) {
        this.record[data[0]] = data[1];
      }
    },
    customFieldDataChanged(data) {
      this.record.custom_fields[data[0]] = data[1];
    },
    async getFields() {
      this.loading = true;
      const response = await helpFunctions.getFields(this.page.model, "show_on_update");
      this.tags_required = response.tags_required;
      this.usual_fields = response.usual_fields;
      this.custom_fields = response.custom_fields;
      this.loading = false;
    },
    downloadFile(file) {
      this.loading = true;
      this.$http
          .get(this.$store.getters.appUrl + "v2/files/" + file.id, { responseType: "blob" })
          .then((response) => {
            this.loading = false;
            const blob = new Blob([response.data], { type: file.content_type });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = file.name;
            link.click();
            URL.revokeObjectURL(link.href);
          }).catch((error) => {
            if (this.$store.getters.isLoggedIn) {
              this.$toasted.error(error);
            }
            this.loading = false;
          });
    },
    deleteFile(file) {
      this.$confirm(this.$store.getters.translate("delete_confirmation")).then((res) => {
        if (res) {
          this.loading = true;
          this.$http
              .delete(this.$store.getters.appUrl + "v2/files/" + file.id)
              .then(() => {
                this.loading = false;
                this.$toasted.success(this.$store.getters.translate("successfully_deleted"));
                this.load(this.record.id);
              }).catch((error) => {
                if (this.$store.getters.isLoggedIn) {
                  this.$toasted.error(error);
                }
                this.loading = false;
              });
        }
      });
    },
    addDropFile(e) {
      this.uploadFiles(e.dataTransfer.files);
    },
    saveFiles() {
      if (this.new_files) {
        this.uploadFiles(this.new_files);
      }
    },
    uploadFiles(files) {
      this.loading = true;
      const formData = new FormData();
      if(this.multiple_files) {
        for (let i = 0; i < files.length; i++) {
          formData.append("attachment[]", files[i]);
        }
      }
      else {
        formData.append("attachment", files);
      }
      formData.append("id", this.record.id);
      this.$http
          .post(this.$store.getters.appUrl + "v2/" + this.page.name + "/uploadfiles", formData, {headers: {"Content-Type": "multipart/form-data"}})
          .then(() => {
            this.loading = false;
            this.$toasted.success(this.$store.getters.translate("successfully_saved"));
            this.load(this.record.id);
          }).catch((error) => {
            if (this.$store.getters.isLoggedIn) {
              this.$toasted.error(error);
            }
            this.loading = false;
          });
    },
    canEdit() {
      return this.$can("edit", this.page.model);
    },
    canDelete() {
      return this.$can("delete", this.page.model);
    },
  },
};
</script>